import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Button } from '@iwoca/orion';
import { Form, Formik } from 'formik';
import merge from 'lodash/merge';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import styles from './DirectorsProfile.module.css';
import { EmailInput } from './EmailInput/EmailInput';
import { FormValues } from './form.type';
import {
  CustomerState,
  fetchPutCustomerState,
} from '../../../api/lending/edge';
import {
  useGetSellerOnboardingRequirements,
  useGetStateByStateKey,
} from '../../../api/lending/lapiHooks';
import {
  denormaliseDate,
  normaliseDate,
} from '../../../Buyer/Signup/stateNormalisation';
import { AddressInput } from '../../../components/AddressInput/AddressInput';
import { addressValidator } from '../../../components/AddressInput/addressValidator';
import {
  denormaliseAddress,
  normaliseAddress,
} from '../../../components/AddressInput/normalisation';
import { CompanyInput } from '../../../components/CompanyInput/CompanyInput';
import { Dropdown } from '../../../components/Dropdown/Dropdown';
import { formatDate } from '../../../components/Input/formatDate';
import { Input } from '../../../components/Input/Input';
import { useOnMount } from '../../../hooks/useOnMount';
import {
  companyNameValidator,
  companyNumberValidator,
  tradingNameValidator,
} from '../../../utils/validators';

export const DirectorsProfile = ({
  onSubmit,
}: {
  onSubmit: () => Promise<void> | void;
}) => {
  const { state: customerState, refetchState: fetchCustomerState } =
    useGetStateByStateKey();
  const { fetchSellerOnboardingRequirements: fetchRequirements } =
    useGetSellerOnboardingRequirements();
  const displayEmail = useDisplayEmail(customerState);
  const formValues = formatFormValues(customerState);
  const disabled = !customerState;

  const handleSubmit = async (values: FormValues) => {
    if (!customerState) return;

    tracking.ampli.directorsProfileSubmitted();
    const reFormattedValues = reFormatFormValues(values);
    const newState = createNewState(customerState, reFormattedValues);

    await fetchPutCustomerState({
      stateKey: customerState.state_key!,
      data: { data: newState },
    });

    void fetchRequirements();
    await fetchCustomerState({});

    await onSubmit();
  };

  return (
    <>
      <p data-testid="directorsProfileText" className={styles.directorsText}>
        We need the <b>Director's details</b> to verify your business and make
        sure we send funds to your business securely.
      </p>
      {displayEmail && <EmailInput />}
      <Formik
        enableReinitialize={true}
        onSubmit={(value) => handleSubmit(value)}
        initialValues={formValues}
        validationSchema={validation}
      >
        {({ values }) => (
          <Form className={styles.form}>
            <Input
              label="Phone number"
              name="phoneNumber"
              disabled={disabled}
            />
            <div className={styles.titleRow}>
              <Dropdown name="title" labelText="Title" disabled={disabled}>
                <option value=""></option>
                <option value="mr">Mr</option>
                <option value="ms">Ms</option>
                <option value="mrs">Mrs</option>
                <option value="miss">Miss</option>
                <option value="sir">Sir</option>
                <option value="professor">Professor</option>
                <option value="doctor">Doctor</option>
                <option value="lord">Lord</option>
                <option value="lady">Lady</option>
                <option value="baron">Baron</option>
                <option value="baroness">Baroness</option>
                <option value="reverend">Reverend</option>
              </Dropdown>
              <Input label="First name" name="firstName" disabled={disabled} />
              <Input label="Last name" name="lastName" disabled={disabled} />
            </div>
            <Input
              name="dob"
              label="Date of birth"
              placeholder="DD/MM/YYYY"
              disabled={disabled}
              formatter={formatDate}
            />
            <AddressInput address={values.address!} disabled={disabled} />
            <Dropdown
              labelText="Company type"
              name="companyType"
              disabled={disabled}
            >
              <option value=""></option>
              <option value="limited_liability_company">
                Limited liability company
              </option>
              <option value="sole_trader">Sole trader</option>
              <option value="ordinary_partnership">Ordinary partnership</option>
              <option value="limited_liability_partnership">
                Limited liability partnership
              </option>
              <option value="public_limited_company">
                Public limited company
              </option>
              <option value="other">Other</option>
            </Dropdown>
            <CompanyInput disabled={disabled} allowManual={true} />
            <Button
              variant="primary"
              className={styles.submitButton}
              type="submit"
              disabled={disabled}
            >
              Continue
            </Button>
          </Form>
        )}
      </Formik>
    </>
  );
};

const useDisplayEmail = (customerState?: CustomerState | null) => {
  const [displayEmail, setDisplayEmail] = useState(false);

  useOnMount(() => setDisplayEmail(!customerState));

  return displayEmail;
};

const reFormatFormValues = (values: FormValues) => {
  return {
    ...values,
    companyName: values.companyName === '' ? undefined : values.companyName,
    firstName: values.firstName === '' ? undefined : values.firstName,
    lastName: values.lastName === '' ? undefined : values.lastName,
    phoneNumber: values.phoneNumber === '' ? undefined : values.phoneNumber,
    companyNumber:
      values.companyNumber === '' ? undefined : values.companyNumber,
    tradingName: values.tradingName === '' ? undefined : values.tradingName,
    tradingStartDate:
      values.tradingStartDate === '' ? undefined : values.tradingStartDate,
    dob: values.dob === '' ? undefined : values.dob,
  };
};

function formatFormValues(customerState?: CustomerState | null): FormValues {
  const person = customerState?.application?.people?.[0];
  const company = customerState?.application?.company;

  return {
    title: person?.title,
    firstName: person?.first_name || '',
    lastName: person?.last_name || '',
    phoneNumber: person?.phones?.[0].number || '',
    companyNumber: company?.company_number || '',
    companyName: company?.registered_company_name || '',
    companyType: company?.type,
    tradingName: company?.trading_name || '',
    tradingStartDate: company?.trading_from_date
      ? normaliseDate(company?.trading_from_date)
      : '',
    dob: person?.date_of_birth ? normaliseDate(person?.date_of_birth) : '',
    address: normaliseAddress(customerState),
  };
}

function createNewState(
  initialState: CustomerState,
  formValues: FormValues,
): CustomerState {
  return merge(initialState, {
    application: {
      people: [
        {
          first_name: formValues.firstName,
          last_name: formValues.lastName,
          title: formValues.title,
          residential_addresses: [denormaliseAddress(formValues)],
          date_of_birth: denormaliseDate(formValues.dob!),
          phones: [
            { type: 'primary', number: formValues.phoneNumber, uid: uuidv4() },
          ],
          uid: uuidv4(),
        },
      ],
      company: {
        company_number: formValues.companyNumber,
        trading_name: formValues?.tradingName,
        registered_company_name: formValues?.companyName,
        type: formValues.companyType,
      },
    },
  });
}

const validation = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .test(
      'is-phoneNumber',
      'Not a valid phone number',
      (value) => !!(value && value.length > 9 && value.length < 14),
    ),
  companyName: companyNameValidator,
  companyNumber: companyNumberValidator,
  dob: Yup.string()
    .required('Date of birth required')
    .length(10, 'Not a valid date of birth (dd-mm-yyyy)'),
  companyType: Yup.string().required('Company type is required'),
  address: addressValidator,
  tradingName: tradingNameValidator,
});
